import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { User } from '@/types';
import { AUTH_SERVICE_URL, sitzungErneuern } from './sitzung';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (redirectTo?: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Dev mode mock user for testing
const DEV_MODE = import.meta.env.VITE_DEV_AUTH === 'true';
const MOCK_USER: User = {
  id: 'dev-user-123',
  email: 'dev@elbklar.local',
  name: 'Dev User',
  role: 'admin',
  avatar_url: null,
};

// Session check interval (1 minute)
const SESSION_CHECK_INTERVAL = 60000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    if (DEV_MODE) return MOCK_USER;
    return null;
  });
  const [isLoading, setIsLoading] = useState(!DEV_MODE);
  const [error, setError] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);
  const sessionCheckRef = useRef<number | null>(null);

  /**
   * Refresh session tokens. Derselbe Refresh wie im API-Client (`sitzung.ts`):
   * Trifft die Sitzungsprüfung auf eine 401 der Glocke, läuft nur einer.
   */
  const refreshSession = useCallback(async (): Promise<boolean> => (await sitzungErneuern()) === 'erneuert', []);

  /**
   * Fetch session from auth service.
   * Uses credentials: 'include' to send HTTP-only cookies.
   */
  const fetchSession = useCallback(async (): Promise<User | null> => {
    try {
      // Try auth service first
      const response = await fetch(`${AUTH_SERVICE_URL}/session`, {
        credentials: 'include',
      });

      if (response.ok) {
        const session = await response.json();
        return {
          id: session.user_id,
          email: session.email,
          name: session.name,
          role: session.role,
          avatar_url: session.picture,
        };
      }

      // If 401, try to refresh
      if (response.status === 401) {
        const refreshed = await refreshSession();
        if (refreshed) {
          const retryResponse = await fetch(`${AUTH_SERVICE_URL}/session`, {
            credentials: 'include',
          });
          if (retryResponse.ok) {
            const session = await retryResponse.json();
            return {
              id: session.user_id,
              email: session.email,
              name: session.name,
              role: session.role,
              avatar_url: session.picture,
            };
          }
        }
      }

      // Kein Fallback auf /auth/me der pm-api: die Route gab es dort nie,
      // der Aufruf lief immer in 404 (Audit PM-8).
      return null;
    } catch {
      return null;
    }
  }, [refreshSession]);

  /**
   * Initialize auth state on mount.
   */
  useEffect(() => {
    if (DEV_MODE) return;

    let mounted = true;

    const initAuth = async () => {
      const sessionUser = await fetchSession();

      if (mounted) {
        setUser(sessionUser);
        setIsLoading(false);
      }
    };

    initAuth();

    // Set up periodic session check
    sessionCheckRef.current = window.setInterval(async () => {
      const sessionUser = await fetchSession();
      if (!sessionUser && user) {
        setUser(null);
      }
    }, SESSION_CHECK_INTERVAL);

    return () => {
      mounted = false;
      // Das Ref haelt ein Timer-Handle, keinen DOM-Knoten — im Cleanup soll
      // genau der zuletzt gesetzte Timer abgeraeumt werden, nicht der beim
      // Aufsetzen des Effekts bekannte.
      if (refreshTimeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        window.clearTimeout(refreshTimeoutRef.current);
      }
      if (sessionCheckRef.current) {
        window.clearInterval(sessionCheckRef.current);
      }
    };
    // Die Refs halten Timer-Handles, keine DOM-Knoten — das Aufraeumen im
    // Cleanup ist genau richtig. `user` bleibt bewusst draussen: sonst wuerde
    // der Sitzungs-Timer bei jeder Anmeldung neu aufgesetzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSession]);

  /**
   * Password login (not supported - use Google)
   */
  const login = async (_email: string, _password: string) => {
    setError('Please use Google login');
    throw new Error('Please use Google login');
  };

  /**
   * Redirect to Google OAuth via centralized auth service.
   */
  const loginWithGoogle = useCallback((redirectTo: string = '/dashboard') => {
    setError(null);
    // Build full URL for redirect (Core API needs full URL to redirect back)
    const fullRedirectUrl = redirectTo.startsWith('http')
      ? redirectTo
      : `${window.location.origin}${redirectTo}`;
    const loginUrl = `${AUTH_SERVICE_URL}/login/google?redirect_to=${encodeURIComponent(fullRedirectUrl)}`;
    window.location.href = loginUrl;
  }, []);

  /**
   * Logout - clears cookies via auth service.
   */
  const logout = useCallback(async () => {
    try {
      await fetch(`${AUTH_SERVICE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors
    }

    setUser(null);

    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithGoogle,
        logout,
        refreshSession,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
